<template>
    <v-dialog v-model="deleteInstanceDialog" max-width="700" persistent>
        <template v-slot:activator="{ on: dialog, attrs }">
            <template v-if="$scopedSlots.activator">
                <slot name="activator" :on="dialog" :attrs="attrs" :disabled="disabledButton" :open="openDialog"></slot>
            </template>
            <v-tooltip v-else left>
                <template v-slot:activator="{ on: tooltip }">
                    <v-btn :disabled="disabledButton" small color="error" icon v-on="{ ...tooltip, ...dialog }">
                        <v-icon small>mdi-trash-can-outline</v-icon>
                    </v-btn>
                </template>
                <span>Delete this instance</span>
            </v-tooltip>
        </template>
        <v-card flat>
            <v-card-title class="flex-nowrap align-start">
                <div class="d-flex align-center secondary--text">
                    <span>Instance deletion</span>
                </div>

                <v-spacer></v-spacer>
                <v-btn :disabled="deleting" @click="deleteInstanceDialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-subtitle>
                <div>{{ instanceName }}</div>
            </v-card-subtitle>
            <v-spacer></v-spacer>

            <v-card-text>
                <v-alert color="error" prominent type="error" class="mt-5" text>
                    <span class="font-weight-bold">Here is what happens when you delete an instance</span>
                    <ul class="mt-3">
                        <li>
                            <span class="subtitle-2">
                                The contents (files, tables, and application) of all states in the instance will be deleted permanently.
                            </span>
                        </li>
                        <li class="subtitle-2">All users of this instance will lose their access and data.</li>
                    </ul>
                    <v-divider class="mt-4 error"></v-divider>
                    <v-checkbox light color="error" v-model="agreeBox">
                        <template v-slot:label>
                            <span>I understand</span>
                        </template>
                    </v-checkbox>
                </v-alert>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <div class="d-flex flex-column">
                    <div class="d-flex align-center justify-end">
                        <DeleteButton
                            buttonName="delete"
                            :objectType="nuvolosObjectTypes.INSTANCE"
                            :objectName="instanceName"
                            :buttonInsideDialog="true"
                            :apiURL="'/instances/' + instanceId"
                            :id="parseInt(instanceId, 10)"
                            :higherLevelId="parseInt($route.params.sid, 10)"
                            warningText="You are about to delete this instance with all its contents permanently. This means that all your data and the snapshots related to this instance will be removed."
                            @error="errorMessage($event.error, $event.backendError)"
                            @deleting="setDeletingStatus($event.value)"
                            :disabled="!agreeBox"
                            fetchString="spaceStore/fetchSpaceInstances"
                            :routeAfter="
                                instanceId.toString() === $route.params.iid.toString()
                                    ? {
                                          name: 'snapshot-overview',
                                          params: { oid: $route.params.oid, sid: $route.params.sid, iid: masterInstanceId, snid: masterDevelopmentSnapshotId }
                                      }
                                    : null
                            "
                            :isAsyncDeletion="true"></DeleteButton>
                    </div>
                    <div>
                        <v-alert v-if="error && errorContent" color="error" icon="warning" class="mt-4" text>
                            <div class="d-flex flex-column">
                                <span class="font-weight-bold">{{ errorContent }}</span>
                                <span>
                                    For more information on instance deletion issues, check the troubleshooting documentation
                                    <v-btn
                                        small
                                        text
                                        color="error"
                                        href="https://docs.nuvolos.com/troubleshooting/troubleshooting/administration-troubleshooting/i-cant-delete-an-instance"
                                        target="_blank">
                                        here
                                    </v-btn>
                                </span>
                            </div>
                        </v-alert>
                    </div>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { enumsData } from '@/mixins/enums'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as instanceErrors from '@/modules/instance/errors'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'

const { getBackendErrorMessage } = createBackendErrorHandler(instanceErrors, lifecycleErrors, permissionsErrors)
const DeleteButton = () => import('@/components/DeleteButton')

export default {
    name: 'InstanceDeleteDialog',
    mixins: [enumsData],
    components: {
        DeleteButton
    },
    props: {
        instanceId: Number,
        instanceName: String,
        masterInstanceId: String,
        masterDevelopmentSnapshotId: String
    },
    data() {
        return {
            error: false,
            errorContent: 'An error has occurred',
            loading: false,
            agreeBox: false,
            deleting: false,
            deleteInstanceDialog: false
        }
    },
    computed: {
        disabledButton() {
            if (this.instanceName && [this.instanceFixedNames.MASTER_INSTANCE, this.instanceFixedNames.DISTRIBUTED_INSTANCE].includes(this.instanceName)) {
                return true
            }
            return false
        }
    },
    methods: {
        openDialog() {
            if (this.disabledButton) {
                return
            }
            this.deleteInstanceDialog = true
        },
        errorMessage: function (error, backendError) {
            if (error) {
                this.error = true
                this.errorContent = getBackendErrorMessage(backendError, 'An error has occurred')
            }
        },
        setDeletingStatus: function (deletingStatus) {
            if (deletingStatus) {
                this.deleting = deletingStatus
            }
        }
    }
}
</script>
